import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DelayedLinearProgress from "@/components/DelayedLinearProgress";
import { services } from "@/injectServices";
import { useLoading } from "@/hooks/Loading";
import {
  useAuthentication,
  useTrackPerfume,
  useCreateNewPerfume,
  useCreatePerfume,
} from "@/services";
import { useNow } from "@/hooks/useNow";
import { PerfumeTunnel } from "@/components/PerfumeTunnel";
import { PerfumeDetails } from "@/components/PerfumeDetails";
import Stack from "@mui/material/Stack";
import {
  PerfumeTunnelPageStepId,
  adaptPerfumeTunnelPageStepId,
  isPerfumeEditable,
  isPerfumeEmpty,
  isPerfumeStaleEmpty,
  userCanEditPerfume,
  userIsAdmin,
} from "@/models";
import { useTranslation } from "react-i18next";

export function PerfumeTunnelPage() {
  const [pageLoading, pageLoadingDispatcher] = useLoading();

  const { workshopLocationId, perfumeId, step } = useParams();

  const service = services().perfumeService;

  // only the workshop devices (tablet role) and admins create, edit or send a perfume; a guest
  // only reads it. Same check in firestore.rules
  const { user, authLoading } = useAuthentication(services().authenticationService);
  const canEdit = userCanEditPerfume(user);

  const { cantCreatePerfumeError } = useCreatePerfume({
    service,
    loadingDispatcher: pageLoadingDispatcher,
    workshopLocationId: workshopLocationId ?? null,
    perfumeId: perfumeId ?? null,
    canCreatePerfume: authLoading ? null : canEdit,
  });

  const { perfume } = useTrackPerfume({
    service,
    loadingDispatcher: pageLoadingDispatcher,
    perfumeId: perfumeId ?? null,
  });

  useEffect(() => {
    const suffix = perfume?.formulaNumber ? `: #${perfume.formulaNumber}` : "";
    document.title = `Galimard Workshop${suffix}`;
  }, [perfume?.formulaNumber]);

  // A locked perfume (or older than the edit window) is read only, so the next client of a shared
  // device can't modify it using the browser history. See docs/formula-edit-protection.md
  const now = useNow(60_000);
  const editable =
    perfume !== null && canEdit && isPerfumeEditable({ perfume, now, isAdmin: userIsAdmin(user) });

  // the perfume the client has started to edit on this page: it stays in the tunnel even if the
  // saved perfume still looks empty (input not saved yet)
  const [editStartedOn, setEditStartedOn] = useState<string | null>(null);
  const editStarted = perfume !== null && editStartedOn === perfume.recipeId;
  const recipeId = perfume?.recipeId ?? null;
  const handleEditStarted = useCallback(() => setEditStartedOn(recipeId), [recipeId]);

  // an empty perfume prepared a while ago (device left ready for the next client): the client
  // must start from a new perfume, with a fresh creation date and edit window
  const staleEmpty =
    perfume !== null &&
    isPerfumeStaleEmpty({ perfume, now, isAdmin: userIsAdmin(user), editStarted });

  const lockPerfumeRef = useRef<(() => Promise<void>) | null>(null);
  const registerLockPerfume = useCallback((lockPerfume: (() => Promise<void>) | null) => {
    lockPerfumeRef.current = lockPerfume;
  }, []);

  const { createNewPerfume, canCreateNewPerfume } = useCreateNewPerfume({
    service,
    loadingDispatcher: pageLoadingDispatcher,
    workshopLocationId: perfume?.workshopLocation?.workshopLocationId,
    lockCurrentPerfume: async () => {
      try {
        await lockPerfumeRef.current?.();
      } catch (error) {
        // must not prevent the next client from creating a perfume
        console.error("Can't lock current perfume", error);
      }
    },
  });

  // the formula being edited gets locked by "new perfume": ask first. Nothing to lose on a locked
  // or empty perfume the client did not touch
  const tunnelShown = perfume !== null && editable && !staleEmpty;
  const [confirmNewPerfumeOpen, setConfirmNewPerfumeOpen] = useState(false);
  const handleNewPerfumeClick = () => {
    if (tunnelShown && (editStarted || !isPerfumeEmpty(perfume))) {
      setConfirmNewPerfumeOpen(true);
    } else {
      createNewPerfume();
    }
  };
  const handleConfirmNewPerfume = () => {
    setConfirmNewPerfumeOpen(false);
    createNewPerfume();
  };

  const activePageNumber =
    adaptPerfumeTunnelPageStepId(step) ?? PerfumeTunnelPageStepId.Identification;

  const { t } = useTranslation();
  return (
    <Container component="main" maxWidth="md" sx={{ mb: 4 }}>
      <DelayedLinearProgress loading={pageLoading || authLoading} />
      <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
        {canEdit && (
          <Stack direction="row" sx={{ justifyContent: "end" }}>
            <Button
              disabled={!canCreateNewPerfume}
              color="secondary"
              onClick={handleNewPerfumeClick}
            >
              {t("creerUnNouveauParfum")}
            </Button>
          </Stack>
        )}
        <Dialog open={confirmNewPerfumeOpen} onClose={() => setConfirmNewPerfumeOpen(false)}>
          <DialogContent>
            <DialogContentText>{t("confirmNewPerfume")}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmNewPerfumeOpen(false)}>{t("cancel")}</Button>
            <Button variant="contained" onClick={handleConfirmNewPerfume}>
              {t("creerUnNouveauParfum")}
            </Button>
          </DialogActions>
        </Dialog>
        <Typography component="h1" variant="h4" align="center">
          {t("atelierDeCreation")}
        </Typography>
        {perfume && tunnelShown && (
          <PerfumeTunnel
            key={perfume.recipeId}
            backendPerfume={perfume}
            activePageNumber={activePageNumber}
            registerLockPerfume={registerLockPerfume}
            onEditStarted={handleEditStarted}
          />
        )}

        {perfume && editable && staleEmpty && (
          <Stack spacing={3} sx={{ my: 4, alignItems: "center" }}>
            <Alert severity="info">{t("perfumeStaleEmpty")}</Alert>
            <Button
              disabled={!canCreateNewPerfume}
              variant="contained"
              size="large"
              onClick={createNewPerfume}
            >
              {t("creerUnNouveauParfum")}
            </Button>
          </Stack>
        )}

        {perfume && !authLoading && !editable && (
          <>
            <Alert severity="info" sx={{ my: 2 }}>
              {canEdit ? t("perfumeLocked") : t("perfumeStaffOnly")}
            </Alert>
            {/* read only formula; PerfumeDetails must stay without personal data (name, email):
                this page can be reached from the history of a shared device */}
            <PerfumeDetails perfume={perfume} showComponents />
          </>
        )}

        {cantCreatePerfumeError && <Alert severity="warning">{cantCreatePerfumeError}</Alert>}
      </Paper>
    </Container>
  );
}
